import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Banner, Button, Card, PageHeader, Pill, type Tone } from '@elysian/ui';
import type { AuditEntry, AuditFilter, AuditKind, AuditPerson } from '../../data/source';
import { useAsync } from '../../data/useAsync';
import { useSession } from '../../state/session';

const KIND: Record<AuditKind, string> = {
  document: 'Document', journal: 'Journal', contact: 'Contact', account: 'Account', vat_code: 'VAT code',
  product: 'Item', settings: 'Settings', user: 'User', period: 'Period', recurring: 'Recurring',
};
const ACTION: Record<AuditEntry['action'], { verb: string; tone: Tone }> = {
  created: { verb: 'added', tone: 'ok' }, changed: { verb: 'changed', tone: 'info' }, deleted: { verb: 'deleted', tone: 'danger' },
  posted: { verb: 'posted', tone: 'ok' }, voided: { verb: 'voided', tone: 'danger' }, reversed: { verb: 'reversed', tone: 'warn' },
  closed: { verb: 'closed', tone: 'draft' }, reopened: { verb: 'reopened', tone: 'warn' }, locked: { verb: 'locked', tone: 'draft' },
};

// Field names as people say them; anything else is the column name in words.
const FIELD: Record<string, string> = {
  doc_no: 'Number', issue_date: 'Date', due_date: 'Due', net_total: 'Net', tax_total: 'VAT', supplier_ref: 'Their number',
  fx_rate: 'Exchange rate', vat_number: 'VAT number', payment_terms_days: 'Payment terms (days)', is_active: 'Switched on',
  is_customer: 'Customer', is_supplier: 'Supplier', email_invoices_to: 'Invoices to', email_statements_to: 'Statements to', people: 'Contact people', notes: 'Notes', payment_terms_basis: 'Payment terms from',
  sales_price: 'Sales price', purchase_price: 'Purchase price', sales_account: 'Sales account', purchase_account: 'Purchase account',
  sales_tax_code: 'Sales VAT', purchase_tax_code: 'Purchase VAT', default_tax_code: 'Default VAT', effective_from: 'Starting',
  journal_no: 'Number', journal_date: 'Date', locked_reason: 'Why', access_ends: 'Access', access_ends_at: 'Access ends', iban: 'IBAN', bic: 'BIC',
  is_closed: 'Closed', display_name: 'Name', legal_name: 'Legal name', trading_name: 'Trading name', registered_number: 'Company number',
  vat_registered: 'VAT registered', vat_frequency: 'VAT returns', vat_period_start_month: 'VAT periods start', secret_hash: 'Secret',
};
const fieldName = (k: string) => FIELD[k] ?? (k.charAt(0).toUpperCase() + k.slice(1)).replace(/_/g, ' ');

const dateTime = (s: string) => new Date(s);
const dayKey = (s: string) => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Dublin' }).format(dateTime(s));
const dayLabel = (s: string) => new Intl.DateTimeFormat('en-IE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/Dublin' }).format(dateTime(s));
const timeOf = (s: string) => new Intl.DateTimeFormat('en-IE', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Dublin' }).format(dateTime(s));

function show(field: string, v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (Array.isArray(v)) return v.length ? v.map((x) => show(field, x)).join(', ') : '—';
  if (typeof v === 'object') return 'details';
  if (field === 'rate' && typeof v === 'number') return `${+(v * 100).toFixed(3)}%`;
  if (typeof v === 'number' && /price|total|amount|balance/.test(field)) return v.toFixed(2);
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) {
    return new Intl.DateTimeFormat('en-IE', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${v}T12:00:00Z`));
  }
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) {
    return new Intl.DateTimeFormat('en-IE', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(new Date(v));
  }
  return String(v);
}

function Changes({ entry }: { entry: AuditEntry }) {
  const keys = Object.keys(entry.changes ?? {});
  if (!keys.length) return null;
  return (
    <dl className="el-audit-changes">
      {keys.map((k) => {
        const c = entry.changes[k] as { from?: unknown; to?: unknown };
        return (
          <div key={k}>
            <dt>{fieldName(k)}</dt>
            <dd>
              {!('from' in c) ? show(k, c.to) : !('to' in c) ? <s>{show(k, c.from)}</s> : (
                <><span className="el-audit-from">{show(k, c.from)}</span><span aria-label="changed to"> → </span>{show(k, c.to)}</>
              )}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

export function AuditTrail() {
  const { source, can } = useSession();
  const [params, setParams] = useSearchParams();
  const filter: AuditFilter = {
    person: params.get('person') || undefined,
    kind: (params.get('kind') as AuditKind | null) || undefined,
    from: params.get('from') || undefined,
    to: params.get('to') || undefined,
    q: params.get('q') || undefined,
    entity: params.get('entity') || undefined,
  };
  const key = params.toString();
  const allowed = can('audit.read');
  const [words, setWords] = useState(filter.q ?? '');
  const [older, setOlder] = useState<{ entries: AuditEntry[]; next: number | null; loading: boolean }>({ entries: [], next: null, loading: false });
  const people = useAsync<AuditPerson[]>(() => (allowed ? source.auditPeople() : Promise.resolve([])), [source, allowed]);
  const page = useAsync(() => (allowed ? source.auditTrail(filter) : Promise.resolve({ entries: [], next_before: null })),
    [source, key, allowed], { keep: true });

  // A fresh first page resets whatever older pages were loaded.
  useEffect(() => {
    if (page.state === 'ready') setOlder({ entries: [], next: page.data.next_before ?? null, loading: false });
  }, [page]);

  function set(k: string, v: string) {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (v) next.set(k, v); else next.delete(k);
      return next;
    }, { replace: true });
  }

  // Typing searches after a pause, so each keystroke isn't a request.
  useEffect(() => {
    const t = setTimeout(() => {
      if ((params.get('q') ?? '') !== words.trim()) set('q', words.trim());
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [words]);

  async function loadOlder() {
    if (older.next == null) return;
    setOlder((o) => ({ ...o, loading: true }));
    try {
      const more = await source.auditTrail({ ...filter, before: older.next });
      setOlder((o) => ({ entries: [...o.entries, ...more.entries], next: more.next_before ?? null, loading: false }));
    } catch {
      setOlder((o) => ({ ...o, loading: false }));
    }
  }

  if (!allowed) {
    return (
      <>
        <PageHeader title="Audit trail" />
        <Banner tone="info">The audit trail is open to owners, admins and accountants. Ask the owner if you need to see it.</Banner>
      </>
    );
  }

  const entries = page.state === 'ready' ? [...page.data.entries, ...older.entries] : [];
  const days: { key: string; label: string; entries: AuditEntry[] }[] = [];
  for (const e of entries) {
    const k = dayKey(e.at);
    const last = days[days.length - 1];
    if (last && last.key === k) last.entries.push(e); else days.push({ key: k, label: dayLabel(e.at), entries: [e] });
  }
  const filtered = Object.values(filter).some(Boolean);
  const subject = filter.entity ? entries[0]?.label : null;

  return (
    <>
      <PageHeader title="Audit trail"
        subtitle="Every change to the books: who made it, when, and what it was before. Entries can’t be edited or deleted." />
      {page.state === 'error' && <Banner>Couldn’t load the audit trail: {page.error.message}</Banner>}
      {filter.entity && (
        <Banner tone="info">
          History of {subject ?? 'one record'}.{' '}
          <button type="button" className="el-linkbtn" onClick={() => set('entity', '')}>Show everything</button>
        </Banner>
      )}
      <Card padding="0">
        <div className="el-row el-audit-filters" role="search" aria-label="Filter the audit trail">
          <select className="el-input" aria-label="Person" value={filter.person ?? ''} onChange={(e) => set('person', e.target.value)}>
            <option value="">Everyone</option>
            {(people.state === 'ready' ? people.data : []).map((p) => <option key={p.actor} value={p.actor}>{p.name}</option>)}
          </select>
          <select className="el-input" aria-label="Kind" value={filter.kind ?? ''} onChange={(e) => set('kind', e.target.value)}>
            <option value="">Everything</option>
            {(Object.keys(KIND) as AuditKind[]).map((k) => <option key={k} value={k}>{KIND[k]}s</option>)}
          </select>
          <label className="el-audit-date"><span>From</span>
            <input type="date" className="el-input" aria-label="From" value={filter.from ?? ''} onChange={(e) => set('from', e.target.value)} />
          </label>
          <label className="el-audit-date"><span>To</span>
            <input type="date" className="el-input" aria-label="To" value={filter.to ?? ''} onChange={(e) => set('to', e.target.value)} />
          </label>
          <input type="search" className="el-input el-audit-search" aria-label="Search the trail" placeholder="Search, e.g. INV-000412 or a name"
            value={words} onChange={(e) => setWords(e.target.value)} />
          {filtered && <Button size="sm" variant="ghost" onClick={() => { setWords(''); setParams(new URLSearchParams(), { replace: true }); }}>Clear</Button>}
        </div>

        {page.state === 'loading' && <div className="el-skeleton" style={{ height: 160, margin: 20 }} aria-label="Loading the audit trail" />}
        {page.state === 'ready' && entries.length === 0 && (
          <p className="el-muted el-audit-empty">{filtered ? 'Nothing matches those filters.' : 'Nothing has been changed yet.'}</p>
        )}
        <div aria-label="Audit trail" role="feed" aria-busy={page.state === 'loading' || older.loading}>
          {days.map((d) => (
            <section key={d.key} aria-label={d.label}>
              <h2 className="el-audit-day">{d.label}</h2>
              <ol className="el-audit">
                {d.entries.map((e) => (
                  <li key={e.id} className="el-audit-entry" aria-label={`${e.actor_name} ${ACTION[e.action].verb} ${e.label}`}>
                    <time className="el-audit-time" dateTime={e.at}>{timeOf(e.at)}</time>
                    <div className="el-audit-body">
                      <div className="el-audit-line">
                        <strong>{e.actor_name}</strong>{' '}
                        <span className="el-audit-verb">{ACTION[e.action].verb}</span>{' '}
                        <Pill tone={ACTION[e.action].tone}>{KIND[e.kind]}</Pill>{' '}
                        <span className="el-audit-label">{e.label}</span>
                      </div>
                      <Changes entry={e} />
                    </div>
                    {e.entity_id && !filter.entity && (
                      <button type="button" className="el-linkbtn el-audit-history" onClick={() => { set('entity', e.entity_id!); window.scrollTo?.({ top: 0 }); }}
                        aria-label={`History of ${e.label}`}>History</button>
                    )}
                  </li>
                ))}
              </ol>
            </section>
          ))}
        </div>
        {older.next != null && (
          <div className="el-audit-more">
            <Button variant="ghost" disabled={older.loading} onClick={() => void loadOlder()}>{older.loading ? 'Loading…' : 'Show older entries'}</Button>
          </div>
        )}
      </Card>
    </>
  );
}
